import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchBlogs } from "../../slice/blogSlice";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Reveal from "../../components/Reveal";
import { Helmet } from "react-helmet-async";
import { MdArrowForward } from "react-icons/md";
import AutoInternalLinks from "../../components/AutoInternalLinks";


// ─────────────────────────────────────────────
// STATIC FALLBACK BLOG CARDS
// Shown when admin has added no blogs yet
// ─────────────────────────────────────────────
import { fallbackBlogs } from "./fallbackBlogsData";




// ─────────────────────────────────────────────
// BLOG CARD (shared between API + fallback)
// ─────────────────────────────────────────────
const BlogCard = ({ blog }) => {
  const blogId = blog?._id || blog?.id;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-[#008300]/10 hover:-translate-y-2 flex flex-col"
    >
      {/* IMAGE */}
      <div className="w-full h-60 sm:h-64 relative overflow-hidden bg-gray-100 flex-shrink-0">
        {blog.image ? (
          <Link to={`/blog/${blogId}`}>
            <img
              src={blog.image}
              alt={`${blog.title} — Novarsis Technology Blog`}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
          </Link>
        ) : null}

        {/* Fallback Placeholder (shown if no image path exists or if the image fails to load) */}
        <div
          className="absolute inset-0 bg-gradient-to-br from-[#008300]/80 to-[#004d00] items-center justify-center p-4"
          style={{ display: blog.image ? "none" : "flex" }}
        >
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg,#00ff00 0,#00ff00 1px,transparent 0,transparent 50%)",
              backgroundSize: "10px 10px",
            }}
          />
          <p className="relative z-10 text-white text-center font-bold text-sm leading-snug">
            {blog.title}
          </p>
        </div>
      </div>

      {/* CONTENT */}
      <div className="p-6 space-y-4 flex flex-col flex-1">
        <Link to={`/blog/${blogId}`} className="flex-1">
          <h3 className="text-lg font-bold text-black hover:text-[#008300] transition leading-snug">
            {blog.title}
          </h3>
        </Link>

        <Link to={`/blog/${blogId}`}>
          <span className="inline-flex items-center gap-2 text-[#008300] font-semibold hover:gap-3 transition-all">
            Read Full Article <MdArrowForward size={18} />
          </span>
        </Link>
      </div>
    </motion.div>
  );
};

// ─────────────────────────────────────────────
// MAIN COMPONENT

// ─────────────────────────────────────────────
const BlogSection = () => {
  const dispatch = useDispatch();
  const { blogs, loading } = useSelector((state) => state.blog);

  useEffect(() => {
    dispatch(fetchBlogs());
  }, [dispatch]);

  // Decide which cards to show
  const displayBlogs =
    Array.isArray(blogs) && blogs.length > 0 ? blogs : fallbackBlogs;
  const isUsingFallback = !(Array.isArray(blogs) && blogs.length > 0);

  return (
    <>
      {/* ─── SEO HELMET ─── */}
      <Helmet>
        <title>Software Development Blog &amp; Tech Insights | Novarsis Technology</title>

        <meta
          name="description"
          content="Novarsis Technology software development blog shares web development, digital marketing, SEO tips, and IT solutions insights to help businesses grow online."
        />
        <meta
          name="keywords"
          content="software development blog, Novarsis Technology blog, web development insights, SEO tips India, digital marketing articles, IT solutions blog"
        />
        <link rel="canonical" href="https://novarsistech.com/blog" />
        <meta name="robots" content="index, follow" />

        <meta property="og:type" content="website" />
        <meta property="og:title" content="Software Development Blog | Novarsis Technology" />
        <meta
          property="og:description"
          content="Explore software development, SEO, and digital marketing insights from Novarsis Technology experts."
        />
        <meta property="og:url" content="https://novarsistech.com/blog" />
        <meta property="og:image" content="https://novarsistech.com/Images/novarsis-og-image.jpg" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Novarsis Technology Software Development Blog" />
        <meta name="twitter:description" content="Read modern software development trends, SEO insights and digital growth strategies." />
        <meta name="twitter:image" content="https://novarsistech.com/Images/novarsis-og-image.jpg" />

        <script type="application/ld+json">{`{"@context":"https://schema.org","@type":"Blog","name":"Novarsis Technology Blog","url":"https://novarsistech.com/blog"}`}</script>
        <script type="application/ld+json">{`{"@context":"https://schema.org","@type":"Organization","name":"Novarsis Technology","url":"https://novarsistech.com","logo":"https://novarsistech.com/logo.png"}`}</script>
        <script type="application/ld+json">{`{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Home","item":"https://novarsistech.com"},{"@type":"ListItem","position":2,"name":"Blog","item":"https://novarsistech.com/blog"}]}`}</script>
      </Helmet>

      {/* ─── HERO ─── */}
      <section className="relative w-full h-[40vh] flex items-center justify-center overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src="/Images/banner.mp4"
          autoPlay
          loop
          muted
          playsInline
          poster="/Images/bannerImage.png"
        />
        <div className="absolute inset-0 bg-[#0a0f1a]/70" />

        <div className="relative z-10 text-center text-white px-4 sm:px-6">
          <Reveal>
            <p className="text-gray-300 text-xs sm:text-sm md:text-base lg:text-xl mb-2 md:mb-3">
              <Link to="/" className="hover:text-[#008300] transition">Home</Link>
              <span className="text-[#008300] mx-1">/</span> Blog
            </p>
          </Reveal>
          <Reveal>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
              Software Development Blog &amp; Insights
            </h1>
          </Reveal>
          <Reveal>
            <p className="mt-3 md:mt-4 max-w-2xl mx-auto text-gray-200 text-sm sm:text-base md:text-lg">
              Read the latest articles on web development, mobile app development,
              UI/UX design, SEO optimization, and digital marketing trends.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── SEO INTRO ─── */}
      <section className="bg-white py-14 px-4">
        <div className="max-w-5xl mx-auto text-gray-700 leading-relaxed text-justify">
          <h2 className="text-3xl font-extrabold mb-6 text-black">
            Novarsis Technology Software{" "}
            <span className="text-[#008300]">Development Blog</span>
          </h2>

          <p className="mb-4">
            The Novarsis Technology blog provides expert knowledge about{" "}
            <Link to="/webdeveloper" className="text-[#008300] font-semibold">
              software development services
            </Link>
            , web design, digital marketing strategies, and modern UI/UX trends.
            Our goal is to help businesses understand technology and grow digitally
            with smart solutions.
          </p>

          <h2 className="text-2xl font-extrabold mt-6 mb-3">
            Why Follow Our{" "}
            <span className="text-[#008300]">Technology Insights</span>?
          </h2>

          <p>
            From SEO improvements to performance optimization, our articles simplify
            complex topics and offer real-world solutions. Businesses, startups, and
            entrepreneurs can explore our{" "}
            <Link to="/about" className="text-[#008300] font-semibold">
              technology expertise
            </Link>{" "}
            and stay ahead in the digital industry.
          </p>
        </div>
      </section>


      {/* ─── LATEST TECHNOLOGY INSIGHTS (Blog Cards) ─── */}
      <section className="relative bg-white min-h-screen py-24 px-4 md:px-16 overflow-hidden">
        <h2 className="sr-only">Latest Software Development Blogs and SEO Insights</h2>

        <div className="relative z-10 max-w-7xl mx-auto">
          {/* Heading */}
          <div className="text-center mb-16">
            <Reveal>
              <h2 className="text-4xl md:text-5xl font-extrabold text-black mb-4">
                Latest <span className="text-[#008300]">Technology Insights</span>
              </h2>
            </Reveal>
            <Reveal>
              <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                Explore software development, SEO tips, and digital marketing knowledge
                from Novarsis Technology experts.
              </p>
            </Reveal>
          </div>

          {/* Loading state */}
          {loading && (
            <div className="flex justify-center py-20">
              <div className="w-10 h-10 border-4 border-[#008300] border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Blog cards grid */}
          {!loading && (
            <>
              {isUsingFallback && (
                <p className="text-center text-sm text-gray-400 mb-8 italic">
                  Sample articles — check back soon for the latest posts.
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
                {displayBlogs.map((blog) => (
                  <BlogCard key={blog?._id || blog?.id} blog={blog} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* AUTO INTERNAL LINKS */}
      <AutoInternalLinks />
    </>
  );
};

export default BlogSection;
